// src/Api/ServicerequestsApi.js
import { db } from '../firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { storage } from "../firebaseConfig";
import { authFetch } from "../Utils/authFetch";

const API_BASE_URL = process.env.REACT_APP_API_URL;

async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function buildError(res, data, fallbackCode) {
  const code = data?.error || fallbackCode || "request_failed";
  const e = new Error(code);
  e.code = code;
  e.httpStatus = res?.status;
  // ✅ conflictissa backend palauttaa server: { ... }
  if (res?.status === 409 && data?.error === "conflict") {
    e.code = "conflict";
    e.server = data?.server || null;
  }
  return e;
}

/**
 * Kuuntele yhden palvelupyynnön viestit reaaliaikaisesti.
 * - Palauttaa unsubscribe-funktion.
 * - Hakee viimeisimmät N viestiä (voit myöhemmin lisätä paginationin).
 */
export function listenMessagesForRequest(
  companyId,
  requestId,
  { pageSize = 200 } = {},
  onData
) {
  if (!companyId || !requestId) return () => {};

  const colRef = collection(
    db,
    "companies",
    String(companyId),
    "serviceRequests",
    String(requestId),
    "messages"
  );

  const q = query(colRef, orderBy("createdAt", "asc"), limit(pageSize));

  const unsub = onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => {
        const data = d.data() || {};
        const createdAt = data.createdAt?.toMillis
          ? data.createdAt.toMillis()
          : (typeof data.createdAt === "number" ? data.createdAt : Date.now());

        return {
          id: d.id, // docId (eli clientMessageId jos käytät sitä)
          body: data.body || "",
          visibility: data.visibility === "internal" ? "internal" : "public",
          senderId: data.senderId || null,
          senderName: data.senderName || "",
          createdAt,
          clientMessageId: data.clientMessageId || d.id,
          isDeleted: data.isDeleted === true,
        };
      });

      onData?.(items, null);
    },
    (err) => {
      onData?.([], err);
    }
  );

  return unsub;
}

/* ---------- LISTA: ETag-conditional ---------- */
export const fetchRequests = async (companyId, { etag } = {}) => {
  const headers = {};
  if (etag) headers["If-None-Match"] = etag;

  const url = `${API_BASE_URL}/fetchServiceRequests?companyId=${encodeURIComponent(
    companyId
  )}`;

  const res = await authFetch(url, { method: "GET", headers });

  if (res.status === 304) {
    return { status: 304 };
  }

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "fetch_requests_failed");
  }

  const newEtag = res.headers.get("ETag");

  return {
    status: 200,
    requests: body?.requests || [],
    version: body?.version || 0,
    updatedAt: body?.updatedAt || null,
    etag: newEtag,
  };
};

/* ---------- DETALJI ---------- */
export const fetchRequestById = async (companyId, requestId) => {
  const url = `${API_BASE_URL}/fetchServiceRequestById?companyId=${encodeURIComponent(
    companyId
  )}&requestId=${encodeURIComponent(requestId)}`;

  const res = await authFetch(url, { method: "GET" });
  const body = await readJsonSafe(res);

  if (!res.ok) {
    throw buildError(res, body, "fetch_request_failed");
  }
  return body; // koko pyyntö + messages + attachments
};

/* ---------- LUO ---------- */
export const createRequest = async ({
  companyId,
  type,
  title,
  apartmentId,
  priority,
  description,
  createdBy, // { name } (uid otetaan backendissä tokenista)
  assigneeName,
}) => {
  const res = await authFetch(`${API_BASE_URL}/createServiceRequest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      companyId,
      type,
      title,
      apartmentId,
      priority,
      description,
      createdBy,
      assigneeName,
    }),
  });

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "create_failed");
  }
  return body; // { id, number }
};

/* ---------- PÄIVITÄ ---------- */
export const updateRequest = async (companyId, requestId, patch, { expectedVersion } = {}) => {
  const payload = { companyId, requestId, patch };

  // ✅ lähetetään expectedVersion vain jos se on annettu
  if (expectedVersion !== undefined && expectedVersion !== null) {
    payload.expectedVersion = expectedVersion;
  }

  const res = await authFetch(`${API_BASE_URL}/updateServiceRequest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "update_failed");
  }

  // backend palauttaa nyt { ok:true, version:<newVersion> }
  return body;
};

/* ---------- VIESTI ---------- */
export const addMessage = async (
  companyId,
  requestId,
  {
    body,
    visibility = "public",
    senderName = "",
    clientMessageId, // ✅ uusi
  }
) => {
  const trimmed = typeof body === "string" ? body.trim() : "";

  if (!companyId || !requestId || !trimmed || !clientMessageId) {
    const e = new Error("missing_fields");
    e.code = "missing_fields";
    throw e;
  }

  const res = await authFetch(`${API_BASE_URL}/addServiceRequestMessage`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      companyId,
      requestId,
      body: trimmed,
      visibility,
      senderName,
      clientMessageId, // ✅ lähetetään backendille
    }),
  });

  const json = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, json, "add_message_failed");
  }

  return {
    // ✅ serverin id pitäisi olla sama kuin clientMessageId (idempotentti)
    id: json?.id || clientMessageId,
    body: json?.body || trimmed,
    visibility: json?.visibility === "internal" ? "internal" : "public",
    senderId: json?.senderId ?? null,
    senderName: json?.senderName ?? senderName ?? "",
    createdAt: typeof json?.createdAt === "number" ? json.createdAt : Date.now(),
  };
};

/* ---------- POISTA VIESTI ---------- */
export const deleteMessage = async (companyId, requestId, messageId) => {
  if (!companyId || !requestId || !messageId) {
    const e = new Error("missing_fields");
    e.code = "missing_fields";
    throw e;
  }

  const res = await authFetch(`${API_BASE_URL}/deleteServiceRequestMessage`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ companyId, requestId, messageId }),
  });

  const json = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, json, "delete_message_failed");
  }

  return {
    ok: true,
    messageId: json?.messageId || messageId,
    deleted: !!json?.deleted,
    alreadyDeleted: !!json?.alreadyDeleted,
  };
};

/* ---------- LIITTEET: client -> Storage, metadata -> backend ---------- */
export const uploadAttachment = async (companyId, requestId, file) => {
  if (!companyId || !requestId || !file) {
    const e = new Error("missing_fields");
    e.code = "missing_fields";
    throw e;
  }

  const safeName = `${Date.now()}_${file.name}`.replace(/\s+/g, "_");
  const storagePath = `companies/${companyId}/requests/${requestId}/attachments/${safeName}`;

  // 1) upload Firebase Storageen (SDK hoitaa authin)
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, file);
  const url = await getDownloadURL(fileRef);

  // 2) rekisteröi metadata backendissä (authFetch → token mukaan)
  const res = await authFetch(`${API_BASE_URL}/registerServiceRequestAttachment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      companyId,
      requestId,
      name: file.name,
      size: file.size,
      url,
      storagePath,
      contentType: file.type || null,
    }),
  });

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "register_attachment_failed");
  }
  return body; // attachment-objekti
};

export const deleteAttachment = async (companyId, requestId, attachmentId) => {
  const res = await authFetch(`${API_BASE_URL}/deleteServiceRequestAttachment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ companyId, requestId, attachmentId }),
  });

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "delete_attachment_failed");
  }
  return body; // { ok:true }
};

/* ---------- POISTA PYYNTÖ ---------- */
export const deleteRequest = async (companyId, requestId) => {
  if (!companyId || !requestId) {
    const e = new Error("missing_fields");
    e.code = "missing_fields";
    throw e;
  }

  const res = await authFetch(`${API_BASE_URL}/deleteServiceRequest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ companyId, requestId }),
  });

  const body = await readJsonSafe(res);
  if (!res.ok) {
    throw buildError(res, body, "delete_failed");
  }
  return body; // { ok:true }
};

/* ---------- EXPORT ---------- */
const ServicerequestsApi = {
  listenMessagesForRequest,
  fetchRequests,
  fetchRequestById,
  createRequest,
  updateRequest,
  addMessage,
  deleteMessage,
  uploadAttachment,
  deleteAttachment,
  deleteRequest,
};

export default ServicerequestsApi;
